/**
 * Histórico de approvals (F11b §6.5.9, ADR-F11-008).
 *
 * Read-only: lista paginada (25/página, D12) com filtros por status e
 * tool_name. Dashboard NUNCA mostra botões Confirmar/Cancelar/Editar.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../design-system/components/Button';
import { Select } from '../../design-system/components/Select';
import { TextField } from '../../design-system/components/TextField';
import { listApprovals, type ApprovalStatus, type HitlApproval } from './api';
import { ApprovalRow } from './components/ApprovalRow';

const STATUS_FILTERS: { value: ApprovalStatus | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'confirmed', label: 'Confirmadas' },
  { value: 'executed', label: 'Executadas' },
  { value: 'cancelled', label: 'Canceladas' },
  { value: 'expired', label: 'Expiradas' },
  { value: 'failed', label: 'Falhas' },
];

const PER_PAGE = 25;

export function ApprovalHistoryPage() {
  const [statusFilter, setStatusFilter] = useState<ApprovalStatus | ''>('');
  const [toolFilter, setToolFilter] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<HitlApproval[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params: import('./api').ListApprovalsParams = {
      page,
      perPage: PER_PAGE,
    };
    if (statusFilter !== '') params.status = statusFilter;
    if (toolFilter.length > 0) params.toolName = toolFilter;
    listApprovals(params)
      .then((res) => {
        if (cancelled) return;
        setItems(res.data);
        setTotal(res.meta.total);
        setHasMore(res.meta.has_more);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, toolFilter, page]);

  const summary = useMemo(() => {
    if (items.length === 0) return 'Sem registros.';
    const start = (page - 1) * PER_PAGE + 1;
    const end = Math.min(page * PER_PAGE, start + items.length - 1);
    return `${start}–${end} de ${total}`;
  }, [items, page, total]);

  return (
    <div className="approval-history stack">
      <header className="approval-history__header">
        <h1>Aprovações HITL</h1>
        <p className="muted">
          Histórico de sugestões geradas pelo agente e aprovadas/canceladas durante reuniões. Esta
          tela é somente leitura — ações ficam no desktop do closer (ADR-F11-008).
        </p>
      </header>

      <div className="approval-history__filters">
        <Select
          label="Status"
          options={STATUS_FILTERS}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ApprovalStatus | '');
            setPage(1);
          }}
        />
        <TextField
          label="Tool"
          type="search"
          value={toolFilter}
          placeholder="sugerir_acao, leddo_get_…"
          onChange={(e) => {
            setToolFilter(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {loading ? <p className="muted">Carregando…</p> : null}
      {error !== null ? (
        <p className="muted" role="alert">
          Não foi possível carregar: {error}
        </p>
      ) : null}

      {!loading && error === null ? (
        <table className="dash-table" aria-label="Histórico de aprovações HITL">
          <thead>
            <tr>
              <th scope="col">Criada em</th>
              <th scope="col">Tool</th>
              <th scope="col">Preview</th>
              <th scope="col">Status</th>
              <th scope="col">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted">
                  Nenhum registro encontrado.
                </td>
              </tr>
            ) : (
              items.map((a) => <ApprovalRow key={a.id} approval={a} />)
            )}
          </tbody>
        </table>
      ) : null}

      <footer className="approval-history__pagination">
        <span className="muted">{summary}</span>
        <div className="approval-history__pagination-actions">
          <Button
            variant="secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Anterior
          </Button>
          <Button
            variant="secondary"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore || loading}
          >
            Próxima
          </Button>
        </div>
      </footer>
    </div>
  );
}
